import { useEffect, useRef } from 'react'
import './tick-mark.css'

/**
 * The mark a choice is made with — square for any of these, round for one of
 * these — drawn from the tokens. One glyph for a `Choice` in a form and a tick
 * in a list, so a tick reads the same wherever it is met.
 */
export function TickGlyph({
  checked,
  round,
  mixed,
}: {
  checked: boolean
  /** One of these, where picking another replaces it. */
  round?: boolean
  /** Some of what it stands for are ticked, and some are not. */
  mixed?: boolean
}) {
  return (
    <span
      className="tick-mark"
      data-checked={checked ? '' : undefined}
      data-mixed={mixed ? '' : undefined}
      data-shape={round ? 'round' : undefined}
      aria-hidden
    />
  )
}

/**
 * A tick or a pick on its own: a real input, kept for the keyboard and the
 * screen reader, under the glyph. The label is the target, so a caller sizes
 * the target by sizing the label (`.tick`).
 */
export function TickMark({
  type = 'checkbox',
  checked,
  mixed,
  label,
  name,
  disabled,
  onChange,
}: {
  type?: 'checkbox' | 'radio'
  checked: boolean
  mixed?: boolean
  /** What ticking it does, distinct from every other tick on the screen. */
  label: string
  /** The group a radio answers, so only one of them is chosen. */
  name?: string
  disabled?: boolean
  onChange: (checked: boolean) => void
}) {
  const input = useRef<HTMLInputElement>(null)
  /* `indeterminate` is a property with no attribute, and it is what a screen
     reader announces as "mixed" for a native checkbox. */
  useEffect(() => {
    if (input.current) input.current.indeterminate = Boolean(mixed)
  }, [mixed])
  return (
    <label className="tick" data-disabled={disabled ? '' : undefined}>
      <input
        ref={input}
        type={type}
        name={name}
        className="sr-only"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => onChange(e.target.checked)}
      />
      <TickGlyph checked={checked} mixed={mixed} round={type === 'radio'} />
    </label>
  )
}
